/**
 * R2 Kategorie/Satz und R3 Ausschlüsse je Quelle (Sollstellung oder Zahlung).
 *
 * Liefert je Quelle: Ausschlussgrund (oder null), Kategorie und Satz aus resolveVatCategory
 * (oder aus einem SET_CATEGORY-Adjustment), sowie alle Prüfhinweise dieser Quelle
 * (PERIOD_MISSING, CATEGORY_DERIVED, VAT_FLAG_MISMATCH, OPTION_9_ABS_2, AUFTEILUNG_12_11,
 * CUSTOM_ART_TAXABLE, SOURCE_DATA_INVALID, CUSTOM_RATE_*). Ausgeschlossene Quellen bleiben als
 * AUSGESCHLOSSEN-Zeile (Kz NONE) sichtbar.
 *
 * „Anderer Steuersatz“ (SONSTIGER_SATZ, Invariante v2): 20 %/8,1 % sind keine deutschen Sätze – die Vermietung
 * eines Auslandsgrundstücks ist in Deutschland nicht steuerbar (§ 3a Abs. 3 Nr. 1 UStG) → Ausschluss mit HINWEIS.
 * 0 % (Gewerbe ohne USt) ist steuerbar, aber steuerfrei → wie STEUERFREI_4_12 in Kz 48 mit INFO.
 * Ohne gültigen Satz → BLOCKER, damit keine Freigabe mit unklarem Satz erfolgt.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue, isHintDismissed} from '../issues';
import {buildLine, buildSource, periodDeepLink, unitsDeepLink} from '../line';
import {monthStart, resolveLeistungsmonat} from '../month';
import {categoryLabel, formatVatRate, isTaxableCategory, normalizeCustomVatRate, rateOfCategory, resolveVatCategory} from '../resolve-vat-category';
import {toRatePercent} from '../rounding';
import {
    BookingArtLite,
    BookingLite,
    PaymentLite,
    PeriodVatCategory,
    RuleContext,
    VatAdjustmentAction,
    VatAdjustmentTargetType,
    VatIssue,
    VatLine,
    VatLineSource,
    VatMonthSource,
    VatRate,
} from '../types';

export const EXCLUDED_ARTS: ReadonlyArray<BookingArtLite> = ['KAUTION', 'DUNNING_FEES', 'INTEREST_EXPENSES'];
export const TAXABLE_ARTS: ReadonlyArray<BookingArtLite> = ['WARM_MIETE', 'KALT_MIETE', 'BETRIEBSKOSTEN', 'GUTHABEN', 'CUSTOM', 'SP'];
export const HINT_ARTS: ReadonlyArray<BookingArtLite> = ['CUSTOM', 'SP'];

export type ExclusionReason =
    'ART' | 'STATUS_IGNORED' | 'NICHT_STEUERBAR' | 'SONSTIGER_SATZ' | 'DATE_MISSING' | 'AMOUNT_MISSING' | 'ADJUSTMENT_EXCLUDE' | 'DURCHLAUFEND';

export const exclusionLabel = (reason: ExclusionReason): string => {
    switch (reason) {
        case 'ART':
            return 'kein Entgelt (Kaution, Mahngebühr oder Verzugszinsen)';
        case 'STATUS_IGNORED':
            return 'Sollstellung ignoriert';
        case 'NICHT_STEUERBAR':
            return 'nicht steuerbar';
        case 'SONSTIGER_SATZ':
            return 'anderer Steuersatz (kein deutscher Umsatzsteuersatz)';
        case 'DATE_MISSING':
            return 'kein Datum – Leistungsmonat nicht bestimmbar';
        case 'AMOUNT_MISSING':
            return 'kein Betrag';
        case 'ADJUSTMENT_EXCLUDE':
            return 'manuell ausgeschlossen';
        case 'DURCHLAUFEND':
            return 'durchlaufender Posten (§ 10 Abs. 1 S. 5 UStG)';
    }
};

export interface SourceClassification {
    excluded: ExclusionReason | null;
    category: PeriodVatCategory | null;
    rate: VatRate;
    derived: boolean;
    issues: VatIssue[];
    source: VatLineSource;
    periodID: string | null;
}

const CATEGORIES: ReadonlyArray<PeriodVatCategory> = ['STEUERFREI_4_12', 'STEUERPFLICHTIG_19', 'STEUERPFLICHTIG_7', 'NICHT_STEUERBAR'];

const findAdjustment = (ctx: RuleContext, targetType: VatAdjustmentTargetType, targetID: string, action: VatAdjustmentAction) =>
    ctx.adjustments.find(a => a.targetType === targetType && a.targetID === targetID && a.action === action);

const isFiniteNumber = (value: number | null): boolean => value !== null && Number.isFinite(Number(value));

interface CategoryResolution {
    category: PeriodVatCategory | null;
    rate: VatRate;
    derived: boolean;
    issues: VatIssue[];
}

/**
 * „Anderer Steuersatz“ (Invariante v2): Der freie Satz gilt für Sollstellungen und Belege, in der deutschen
 * Voranmeldung aber nicht als steuerpflichtiger Umsatz. Kein Satz → BLOCKER; 0 % → wie § 4 Nr. 12 (Kz 48) mit INFO;
 * sonst Ausschluss mit einem HINWEIS je Mietverhältnis. Die Flag-Prüfung (VAT_FLAG_MISMATCH) entfällt hier bewusst:
 * Sollstellungen mit `vat = 20` sind für dieses Mietverhältnis korrekt.
 */
const resolveCustomRateCategory = (
    periodID: string,
    rate: number | null,
    storedVat: number | null,
    periodLink: string,
    derived: boolean,
    issues: VatIssue[],
    flags: { includesVAT: boolean | null; vat: number | null },
    sourceID: string,
    describe: string
): CategoryResolution => {
    if (rate === null) {
        const stored = storedVat === null || storedVat === undefined ? 'kein Satz' : `${formatVatRate(Number(storedVat))} %`;
        issues.push(createIssue('CUSTOM_RATE_INVALID', periodID,
            `Das Mietverhältnis ist als „anderer Steuersatz“ erfasst, trägt aber keinen gültigen Satz (gespeichert: ${stored}). Bitte im Mietverhältnis einen Satz zwischen 0 und 30 % eintragen oder die Kategorie 19 % bzw. 7 % wählen.`,
            {deepLink: periodLink}));
        return {category: 'SONSTIGER_SATZ', rate: 0, derived, issues};
    }
    // Satzvergleich bleibt: eine Sollstellung mit 19 % auf einer 20-%- oder 0-%-Periode (z. B. Cron-Buchung aus dem
    // Regressionsfenster) soll auffallen – nur die Boolean-Prüfung „mit/ohne MwSt“ entfällt für den freien Satz.
    if (flags.vat !== null && flags.vat !== undefined && normalizeCustomVatRate(flags.vat) !== rate) {
        issues.push(createIssue('VAT_FLAG_MISMATCH', sourceID,
            `${describe} trägt den Satz ${formatVatRate(Number(flags.vat))} %, das Mietverhältnis aber „${categoryLabel('SONSTIGER_SATZ', rate)}“. Bitte die Sollstellung prüfen.`,
            {deepLink: periodLink}));
    }
    if (rate === 0) {
        issues.push(createIssue('CUSTOM_RATE_ZERO_STEUERFREI', periodID,
            'Das Mietverhältnis ist mit 0 % erfasst. Die Miete wird als steuerfreier Umsatz nach § 4 Nr. 12 UStG (Kennzahl 48) geführt; der Gewerbe-Mietvertrag behält seine Regeln.',
            {deepLink: periodLink}));
        return {category: 'STEUERFREI_4_12', rate: 0, derived, issues};
    }
    issues.push(createIssue('CUSTOM_RATE_EXCLUDED', periodID,
        `Das Mietverhältnis ist mit dem Steuersatz ${formatVatRate(rate)} % erfasst. Das ist kein deutscher Umsatzsteuersatz; die Miete wird in dieser Voranmeldung nicht als steuerpflichtiger Umsatz geführt (Grundstück im Ausland: nicht steuerbar, § 3a Abs. 3 Nr. 1 UStG). Für eine inländische Vermietung 19 % oder 7 % wählen.`,
        {deepLink: periodLink}));
    return {category: 'SONSTIGER_SATZ', rate: 0, derived, issues};
};

const resolveCategoryForSource = (
    ctx: RuleContext,
    source: VatLineSource,
    periodID: string | null,
    flags: { includesVAT: boolean | null; vat: number | null },
    describe: string,
    statusDate: Date | null
): CategoryResolution => {
    const issues: VatIssue[] = [];
    const periodLink = source.unitID ? periodDeepLink(source.propertyID, source.unitID) : unitsDeepLink(source.propertyID);

    let category: PeriodVatCategory | null = null;
    let derived = false;

    const override = findAdjustment(ctx, source.type, source.id, 'SET_CATEGORY');
    if (override && CATEGORIES.includes(override.value as PeriodVatCategory)) {
        category = override.value as PeriodVatCategory;
    } else {
        const period = periodID ? ctx.periodsByID.get(periodID) : undefined;
        if (!period) {
            const fallbackRate: VatRate = flags.includesVAT ? (toRatePercent(flags.vat) ?? 19) : 0;
            issues.push(createIssue('PERIOD_MISSING', source.id,
                `Zu ${describe} ist kein Mietverhältnis hinterlegt. Kategorie und Steuersatz können nicht bestimmt werden; vorläufig wurde ${fallbackRate} % aus dem MwSt-Kennzeichen übernommen.`,
                {deepLink: periodLink}));
            return {category: null, rate: fallbackRate, derived: false, issues};
        }
        const resolved = resolveVatCategory(period);
        category = resolved.category;
        derived = resolved.derived;
        if (derived) {
            issues.push(createIssue('CATEGORY_DERIVED', period.id,
                `Die USt-Kategorie „${categoryLabel(category, resolved.rate)}“ wurde aus Gewerbe-Kennzeichen und MwSt-Satz des Mietverhältnisses abgeleitet. Bitte im Mietverhältnis bestätigen.`,
                {deepLink: periodLink}));
        }
        if (category === 'SONSTIGER_SATZ') {
            return resolveCustomRateCategory(period.id, resolved.rate, period.costs?.vat ?? null, periodLink, derived, issues, flags, source.id, describe);
        }
        // R2: profileAt je Zeile – Status am Leistungs- bzw. Zahlungsdatum, nicht am Zeitraumbeginn
        const statusAtSource = ctx.profileAt(statusDate ?? ctx.range.start).status;
        if (statusAtSource === 'REGELBESTEUERUNG') {
            const propertyProfile = ctx.propertyProfiles[source.propertyID];
            if (category === 'STEUERPFLICHTIG_19' && !isHintDismissed(propertyProfile, period.id, 'OPTION_9_ABS_2')) {
                issues.push(createIssue('OPTION_9_ABS_2', period.id,
                    'Die Vermietung ist mit 19 % erfasst. Die Option nach § 9 UStG setzt voraus, dass der Mieter das Objekt ausschließlich für Umsätze nutzt, die den Vorsteuerabzug nicht ausschließen (§ 9 Abs. 2 UStG). Bitte einmalig bestätigen.',
                    {deepLink: periodLink}));
            }
            if (category === 'STEUERPFLICHTIG_7' && !isHintDismissed(propertyProfile, period.id, 'AUFTEILUNG_12_11')) {
                issues.push(createIssue('AUFTEILUNG_12_11', period.id,
                    'Kurzfristige Beherbergung mit 7 %: Nebenleistungen wie Frühstück oder Stellplatz unterliegen 19 % (Aufteilungsgebot, § 12 Abs. 2 Nr. 11 S. 2 UStG). Bitte prüfen und einmalig bestätigen.',
                    {deepLink: periodLink}));
            }
        }
    }

    const rate = rateOfCategory(category);
    if (flags.includesVAT !== null) {
        const expected = isTaxableCategory(category);
        if (flags.includesVAT !== expected) {
            issues.push(createIssue('VAT_FLAG_MISMATCH', source.id,
                `${describe} ist ${flags.includesVAT ? 'mit' : 'ohne'} MwSt erfasst, das Mietverhältnis aber als „${categoryLabel(category)}“. Die Engine rechnet mit der Kategorie.`,
                {deepLink: periodLink}));
        } else if (expected && flags.vat !== null && toRatePercent(flags.vat) !== rate) {
            issues.push(createIssue('VAT_FLAG_MISMATCH', source.id,
                `${describe} trägt den Satz ${flags.vat} %, das Mietverhältnis aber „${categoryLabel(category)}“. Die Engine rechnet mit ${rate} %.`,
                {deepLink: periodLink}));
        }
    }

    return {category, rate, derived, issues};
};

const classifyExclusionByAdjustment = (ctx: RuleContext, targetType: VatAdjustmentTargetType, id: string): ExclusionReason | null => {
    if (findAdjustment(ctx, targetType, id, 'EXCLUDE')) {
        return 'ADJUSTMENT_EXCLUDE';
    }
    if (findAdjustment(ctx, targetType, id, 'DURCHLAUFEND')) {
        return 'DURCHLAUFEND';
    }
    return null;
};

const artHint = (art: BookingArtLite | null, sourceID: string, describe: string): VatIssue | null => {
    if (art !== null && TAXABLE_ARTS.includes(art) && !HINT_ARTS.includes(art)) {
        return null;
    }
    return createIssue('CUSTOM_ART_TAXABLE', sourceID,
        `${describe} hat die Art „${art ?? 'unbekannt'}“ und wird als steuerbares Entgelt behandelt. Bitte bestätigen oder ausschließen.`);
};

export const classifyBooking = (ctx: RuleContext, booking: BookingLite): SourceClassification => {
    const source = buildSource('BOOKING', booking);
    const describe = `Sollstellung „${booking.text ?? booking.id}“`;
    const base: SourceClassification = {excluded: null, category: null, rate: 0, derived: false, issues: [], source, periodID: booking.periodID};

    const byAdjustment = classifyExclusionByAdjustment(ctx, 'BOOKING', booking.id);
    if (byAdjustment) {
        return {...base, excluded: byAdjustment};
    }
    if (booking.art !== null && EXCLUDED_ARTS.includes(booking.art)) {
        return {...base, excluded: 'ART'};
    }
    if (booking.status === 'IGNORED') {
        return {...base, excluded: 'STATUS_IGNORED'};
    }
    if (!isFiniteNumber(booking.amount)) {
        return {
            ...base, excluded: 'AMOUNT_MISSING',
            issues: [createIssue('SOURCE_DATA_INVALID', booking.id, `${describe} hat keinen Betrag.`, {deepLink: source.deepLink})],
        };
    }
    const lm = resolveLeistungsmonat(booking);
    if (!lm) {
        return {
            ...base, excluded: 'DATE_MISSING',
            issues: [createIssue('SOURCE_DATA_INVALID', booking.id, `${describe} hat weder Datum noch Soll-Monat – der Leistungsmonat ist nicht bestimmbar.`, {deepLink: source.deepLink})],
        };
    }

    const resolution = resolveCategoryForSource(ctx, source, booking.periodID, {includesVAT: booking.includesVAT, vat: booking.vat}, describe, monthStart(lm.monthKey));
    const issues = [...resolution.issues];
    if (resolution.category === 'NICHT_STEUERBAR') {
        return {...base, excluded: 'NICHT_STEUERBAR', category: resolution.category, rate: 0, derived: resolution.derived, issues};
    }
    if (resolution.category === 'SONSTIGER_SATZ') {
        return {...base, excluded: 'SONSTIGER_SATZ', category: resolution.category, rate: 0, derived: resolution.derived, issues};
    }
    const hint = artHint(booking.art, booking.id, describe);
    if (hint) {
        issues.push(hint);
    }
    return {...base, category: resolution.category, rate: resolution.rate, derived: resolution.derived, issues};
};

export const classifyPayment = (ctx: RuleContext, payment: PaymentLite): SourceClassification => {
    const booking = payment.bookingID ? ctx.bookingsByID.get(payment.bookingID) : undefined;
    const unitID = payment.unitID ?? booking?.unitID ?? null;
    const periodID = booking?.periodID ?? payment.periodID ?? null;
    const source = buildSource('PAYMENT', {id: payment.id, propertyID: payment.propertyID, unitID, periodID});
    const art = payment.art ?? booking?.art ?? null;
    const describe = `Zahlung „${payment.text ?? booking?.text ?? payment.id}“`;
    const base: SourceClassification = {excluded: null, category: null, rate: 0, derived: false, issues: [], source, periodID};

    const byAdjustment = classifyExclusionByAdjustment(ctx, 'PAYMENT', payment.id);
    if (byAdjustment) {
        return {...base, excluded: byAdjustment};
    }
    if (art !== null && EXCLUDED_ARTS.includes(art)) {
        return {...base, excluded: 'ART'};
    }
    if (!isFiniteNumber(payment.amount)) {
        return {...base, excluded: 'AMOUNT_MISSING', issues: [createIssue('SOURCE_DATA_INVALID', payment.id, `${describe} hat keinen Betrag.`, {deepLink: source.deepLink})]};
    }
    if (!payment.date) {
        return {...base, excluded: 'DATE_MISSING', issues: [createIssue('SOURCE_DATA_INVALID', payment.id, `${describe} hat kein Zahlungsdatum.`, {deepLink: source.deepLink})]};
    }

    const flags = {includesVAT: payment.includesVAT ?? booking?.includesVAT ?? null, vat: payment.vat ?? booking?.vat ?? null};
    const resolution = resolveCategoryForSource(ctx, source, periodID, flags, describe, payment.date);
    const issues = [...resolution.issues];
    if (resolution.category === 'NICHT_STEUERBAR') {
        return {...base, excluded: 'NICHT_STEUERBAR', category: resolution.category, rate: 0, derived: resolution.derived, issues};
    }
    if (resolution.category === 'SONSTIGER_SATZ') {
        return {...base, excluded: 'SONSTIGER_SATZ', category: resolution.category, rate: 0, derived: resolution.derived, issues};
    }
    const hint = artHint(art, payment.id, describe);
    if (hint) {
        issues.push(hint);
    }
    return {...base, category: resolution.category, rate: resolution.rate, derived: resolution.derived, issues};
};

export const linkIssues = (issues: VatIssue[], lineID: string): VatIssue[] =>
    issues.map(issue => ({...issue, lineIDs: issue.lineIDs.includes(lineID) ? issue.lineIDs : [...issue.lineIDs, lineID]}));

export const issueIDsOf = (issues: VatIssue[]): string[] => issues.map(issue => issue.id);

export const buildExcludedLine = (
    classification: SourceClassification,
    monthKey: string,
    monthSource: VatMonthSource,
    gross: number,
    labelPrefix: string
): VatLine => buildLine({
    rule: 'AUSGESCHLOSSEN',
    source: classification.source,
    monthKey,
    monthSource,
    category: classification.category,
    rate: 0,
    gross,
    label: `${labelPrefix} – ${exclusionLabel(classification.excluded ?? 'ADJUSTMENT_EXCLUDE')}`,
    issueIDs: issueIDsOf(classification.issues),
});
